import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../services/api';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [ndsValue, setNdsValue] = useState<string>('');
  const [deflatorValue, setDeflatorValue] = useState<string>('');
  const [apiKeyValue, setApiKeyValue] = useState<string>('');
  const [hasCustomApiKey, setHasCustomApiKey] = useState(false);
  const [apiKeyMasked, setApiKeyMasked] = useState<string | null>(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingNds, setLoadingNds] = useState(false);
  const [loadingDeflator, setLoadingDeflator] = useState(false);
  const [loadingApiKey, setLoadingApiKey] = useState(false);
  const [loadingApiKeyDelete, setLoadingApiKeyDelete] = useState(false);
  const [savedNds, setSavedNds] = useState(false);
  const [savedDeflator, setSavedDeflator] = useState(false);
  const [savedApiKey, setSavedApiKey] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoadingPage(true);
    setError('');
    try {
      const data = await settingsAPI.get();
      console.log('Loaded settings:', data);
      setNdsValue(data.default_nds?.toString() || '20');
      setDeflatorValue(data.default_deflator?.toString() || '1.0');
      setHasCustomApiKey(data.has_custom_api_key || false);
      setApiKeyMasked(data.api_key_masked || null);
    } catch (err: any) {
      console.error('Failed to load settings:', err);
      setError(err.response?.data?.detail || 'Ошибка при загрузке настроек');
      // Set defaults on error
      setNdsValue('20');
      setDeflatorValue('1.0');
    } finally {
      setLoadingPage(false);
    }
  };

  const handleSaveNds = async () => {
    setError('');
    setSavedNds(false);
    setLoadingNds(true);

    try {
      const value = parseFloat(ndsValue);
      if (value < 0 || value > 100) {
        throw new Error('НДС должен быть от 0 до 100');
      }
      const response = await settingsAPI.updateNDS(value);
      console.log('Saved NDS, response:', response);
      // Update local state with server response
      setNdsValue(response.default_nds?.toString() || value.toString());
      setDeflatorValue(response.default_deflator?.toString() || deflatorValue);
      setSavedNds(true);
      setTimeout(() => setSavedNds(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Ошибка при сохранении НДС');
    } finally {
      setLoadingNds(false);
    }
  };

  const handleSaveDeflator = async () => {
    setError('');
    setSavedDeflator(false);
    setLoadingDeflator(true);

    try {
      const value = parseFloat(deflatorValue);
      if (value <= 0) {
        throw new Error('Индекс-дефлятор должен быть больше 0');
      }
      const response = await settingsAPI.updateDeflator(value);
      console.log('Saved deflator, response:', response);
      // Update local state with server response
      setNdsValue(response.default_nds?.toString() || ndsValue);
      setDeflatorValue(response.default_deflator?.toString() || value.toString());
      setSavedDeflator(true);
      setTimeout(() => setSavedDeflator(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Ошибка при сохранении дефлятора');
    } finally {
      setLoadingDeflator(false);
    }
  };

  const handleSaveApiKey = async () => {
    setApiKeyError('');
    setSavedApiKey(false);
    setLoadingApiKey(true);

    try {
      if (!apiKeyValue.trim()) {
        throw new Error('API ключ не может быть пустым');
      }
      const response = await settingsAPI.updateApiKey(apiKeyValue.trim());
      setHasCustomApiKey(response.has_custom_api_key || false);
      setApiKeyMasked(response.api_key_masked || null);
      setApiKeyValue('');
      setSavedApiKey(true);
      setTimeout(() => setSavedApiKey(false), 5000);
    } catch (err: any) {
      setApiKeyError(err.response?.data?.detail || err.message || 'Ошибка при сохранении API ключа');
    } finally {
      setLoadingApiKey(false);
    }
  };

  const handleDeleteApiKey = async () => {
    setApiKeyError('');
    setLoadingApiKeyDelete(true);

    try {
      const response = await settingsAPI.deleteApiKey();
      setHasCustomApiKey(response.has_custom_api_key || false);
      setApiKeyMasked(response.api_key_masked || null);
      setApiKeyValue('');
    } catch (err: any) {
      setApiKeyError(err.response?.data?.detail || err.message || 'Ошибка при удалении API ключа');
    } finally {
      setLoadingApiKeyDelete(false);
    }
  };

  if (loadingPage) {
    return (
      <div>
        <h2 style={{ marginBottom: '20px' }}>{t('settings.title')}</h2>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          maxWidth: '600px',
          textAlign: 'center'
        }}>
          Загрузка настроек...
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>{t('settings.title')}</h2>

      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        maxWidth: '600px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
          {t('settings.defaultValues')}
        </h3>

        {error && (
          <div style={{
            color: '#d32f2f',
            backgroundColor: '#ffebee',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* НДС Setting */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('settings.defaultNds')}
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="number"
              step="0.01"
              value={ndsValue}
              onChange={(e) => setNdsValue(e.target.value)}
              style={{
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                width: '150px',
                fontSize: '16px'
              }}
            />
            <span>%</span>
            <button
              type="button"
              onClick={handleSaveNds}
              disabled={loadingNds}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loadingNds ? 'not-allowed' : 'pointer',
                opacity: loadingNds ? 0.7 : 1
              }}
            >
              {loadingNds ? 'Сохранение...' : 'Сохранить'}
            </button>
            {savedNds && (
              <span style={{ color: '#2e7d32' }}>Сохранено</span>
            )}
          </div>
        </div>

        {/* Deflator Setting */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Индекс-дефлятор по умолчанию
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="number"
              step="0.001"
              value={deflatorValue}
              onChange={(e) => setDeflatorValue(e.target.value)}
              style={{
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                width: '150px',
                fontSize: '16px'
              }}
            />
            <button
              type="button"
              onClick={handleSaveDeflator}
              disabled={loadingDeflator}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loadingDeflator ? 'not-allowed' : 'pointer',
                opacity: loadingDeflator ? 0.7 : 1
              }}
            >
              {loadingDeflator ? 'Сохранение...' : 'Сохранить'}
            </button>
            {savedDeflator && (
              <span style={{ color: '#2e7d32' }}>Сохранено</span>
            )}
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Используется при создании задачи анализа цен, если не указан явно
          </p>
        </div>
      </div>

      {/* API Key Section */}
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        maxWidth: '600px',
        marginTop: '20px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
          API-ключ MWS GPT
        </h3>

        {apiKeyError && (
          <div style={{
            color: '#d32f2f',
            backgroundColor: '#ffebee',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            {apiKeyError}
          </div>
        )}

        {savedApiKey && (
          <div style={{
            color: '#2e7d32',
            backgroundColor: '#e8f5e9',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            Ваш ключ успешно установлен!
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          {hasCustomApiKey ? (
            <span style={{ color: '#2e7d32' }}>
              Текущий ключ: {apiKeyMasked}
            </span>
          ) : (
            <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
              Ключ не установлен
            </span>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Новый API-ключ
          </label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="password"
              value={apiKeyValue}
              onChange={(e) => setApiKeyValue(e.target.value)}
              placeholder="Вставьте API-ключ"
              style={{
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                width: '300px',
                fontSize: '16px'
              }}
            />
            <button
              type="button"
              onClick={handleSaveApiKey}
              disabled={loadingApiKey || !apiKeyValue.trim()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (loadingApiKey || !apiKeyValue.trim()) ? 'not-allowed' : 'pointer',
                opacity: (loadingApiKey || !apiKeyValue.trim()) ? 0.7 : 1
              }}
            >
              {loadingApiKey ? 'Проверка...' : 'Сохранить'}
            </button>
            {hasCustomApiKey && (
              <button
                type="button"
                onClick={handleDeleteApiKey}
                disabled={loadingApiKeyDelete}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loadingApiKeyDelete ? 'not-allowed' : 'pointer',
                  opacity: loadingApiKeyDelete ? 0.7 : 1
                }}
              >
                {loadingApiKeyDelete ? 'Удаление...' : 'Сбросить'}
              </button>
            )}
          </div>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Ключ проверяется при сохранении. Необходим для создания задач анализа.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
